import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import ActiveTenantReportAlert from './reports/ActiveTenantReportAlert';
import { useCampaignReportWebSocket } from '../hooks/useCampaignReportWebSocket';
import { useNumberFormat } from '../hooks/useNumberFormat';
import {
  extractActiveTenantReportFromError,
  extractApiDetailMessage,
  fetchActiveTenantReport,
  requestCampaignReportDownload,
} from '../services/reports';
import type {
  ActiveTenantReportSummary,
  CampaignReportListItem,
  CampaignStats,
} from '../types';
import { formatDateTime } from '../utils/date';

interface ReportRecord extends Omit<CampaignReportListItem, 'reportType' | 'campaignId' | 'campaignName' | 'createdByName' | 'downloadUrl'> {
  fileUrl: string | null;
  statsHash?: string;
}

interface CampaignReportModalProps {
  open: boolean;
  onClose: () => void;
  campaignId?: string;
  stats?: CampaignStats | null;
  onGeneratingChange?: (isGenerating: boolean) => void;
}

export default function CampaignReportModal({
  open,
  onClose,
  campaignId,
  stats,
  onGeneratingChange,
}: CampaignReportModalProps) {
  const { t } = useTranslation(['pages']);
  const { formatNumber, formatPercent, formatDecimal, locale } = useNumberFormat();

  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeReportId, setActiveReportId] = useState<string | null>(null);
  const [activeTenantReport, setActiveTenantReport] = useState<ActiveTenantReportSummary | null>(null);
  const [loadingActiveTenantReport, setLoadingActiveTenantReport] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);
  const [reportToast, setReportToast] = useState<{
    open: boolean;
    message: string;
    severity: 'info' | 'success' | 'error';
  }>({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    onGeneratingChange?.(isGenerating);
  }, [isGenerating, onGeneratingChange]);

  const fetchReports = useCallback(async () => {
    if (!campaignId) {
      return;
    }
    try {
      const res = await api.get(`/api/campaigns/${campaignId}/reports/`);
      const data = res.data;
      if (data && data.length > 0) {
        const mappedReports: ReportRecord[] = data.map((e: Record<string, unknown>) => ({
          id: e.id as string,
          status: e.status as 'pending' | 'processing' | 'completed' | 'failed',
          fileUrl: e.fileUrl as string | null,
          fileName: e.fileName as string | null,
          fileSize: e.fileSize as number | null,
          totalContacts: e.totalContacts as number,
          totalCalls: e.totalCalls as number,
          statsHash: e.statsHash as string | undefined,
          createdAt: e.createdAt as string,
          startedAt: e.startedAt as string | null,
          completedAt: e.completedAt as string | null,
          errorMessage: e.errorMessage as string | null,
          progress: e.progress as number | null,
          processedContacts: e.processedContacts as number | null,
          processedCalls: e.processedCalls as number | null,
          estimatedSecondsRemaining: e.estimatedSecondsRemaining as number | null,
          progressMessage: e.progressMessage as string | null,
        }));
        setReports(mappedReports);
        const latestProcessing = mappedReports.find((report) => report.status === 'pending' || report.status === 'processing');
        setActiveReportId(latestProcessing?.id ?? null);
        const latest = mappedReports[0];
        if (latest.status === 'pending' || latest.status === 'processing') {
          setIsGenerating(true);
        } else {
          setIsGenerating(false);
        }
      } else {
        setReports([]);
        setActiveReportId(null);
        setIsGenerating(false);
      }
    } catch (err) {
      console.warn('Failed to fetch reports:', err);
    }
  }, [campaignId]);

  const fetchActiveTenantReportSummary = useCallback(async () => {
    setLoadingActiveTenantReport(true);
    try {
      const report = await fetchActiveTenantReport();
      setActiveTenantReport(report);
    } catch (err) {
      console.warn('Failed to fetch active tenant report:', err);
      setActiveTenantReport(null);
    } finally {
      setLoadingActiveTenantReport(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchReports();
      fetchActiveTenantReportSummary();
    } else {
      setActiveTenantReport(null);
      setLoadingActiveTenantReport(false);
    }
  }, [open, fetchReports, fetchActiveTenantReportSummary]);

  const handleDownload = useCallback(async (reportId: string) => {
    try {
      setDownloadingReportId(reportId);
      const downloadUrl = await requestCampaignReportDownload(reportId);
      if (!downloadUrl) {
        throw new Error('download_url_missing');
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to request campaign report download:', error);
      setReportToast({
        open: true,
        message: extractApiDetailMessage(error) || t('pages:campaigns.detail.export.toast.exportError'),
        severity: 'error',
      });
    } finally {
      setDownloadingReportId((current) => (current === reportId ? null : current));
    }
  }, [t]);

  useCampaignReportWebSocket(open ? activeReportId ?? undefined : undefined, {
    onReportUpdated: (event) => {
      setReports((prev) => {
        const existing = prev.find((report) => report.id === event.id);
        const updated: ReportRecord = existing
          ? {
              ...existing,
              status: event.status,
              fileUrl: event.file_url ?? existing.fileUrl,
              fileName: event.file_name ?? existing.fileName,
              fileSize: event.file_size ?? existing.fileSize,
              totalContacts: event.total_contacts ?? existing.totalContacts,
              totalCalls: event.total_calls ?? existing.totalCalls,
              statsHash: event.stats_hash ?? existing.statsHash,
              createdAt: event.created_at ?? existing.createdAt,
              startedAt: event.started_at ?? existing.startedAt,
              completedAt: event.completed_at ?? existing.completedAt,
              errorMessage: event.error_message ?? existing.errorMessage,
              progress: event.progress ?? existing.progress,
              processedContacts: event.processed_contacts ?? existing.processedContacts,
              processedCalls: event.processed_calls ?? existing.processedCalls,
              estimatedSecondsRemaining: event.estimated_seconds_remaining ?? existing.estimatedSecondsRemaining,
              progressMessage: event.progress_message ?? existing.progressMessage,
            }
          : {
              id: event.id,
              status: event.status,
              fileUrl: event.file_url ?? null,
              fileName: event.file_name ?? null,
              fileSize: event.file_size ?? null,
              totalContacts: event.total_contacts ?? 0,
              totalCalls: event.total_calls ?? 0,
              statsHash: event.stats_hash ?? undefined,
              createdAt: event.created_at ?? new Date().toISOString(),
              startedAt: event.started_at ?? null,
              completedAt: event.completed_at ?? null,
              errorMessage: event.error_message ?? null,
              progress: event.progress ?? null,
              processedContacts: event.processed_contacts ?? null,
              processedCalls: event.processed_calls ?? null,
              estimatedSecondsRemaining: event.estimated_seconds_remaining ?? null,
              progressMessage: event.progress_message ?? null,
            };

        const next = existing
          ? prev.map((report) => (report.id === event.id ? updated : report))
          : [updated, ...prev];
        return next;
      });
      if (event.status === 'pending' || event.status === 'processing') {
        setIsGenerating(true);
        setActiveReportId(event.id);
      } else {
        setIsGenerating(false);
      }
    },
    onReportReady: (_) => {
      setIsGenerating(false);
      setActiveReportId(null);
      setActiveTenantReport(null);
      fetchReports();
      setReportToast({
        open: true,
        message: t('pages:campaigns.detail.export.toast.exportReady'),
        severity: 'success',
      });
    },
    onReportFailed: (event) => {
      setIsGenerating(false);
      setActiveReportId(null);
      setActiveTenantReport(null);
      fetchReports();
      setReportToast({
        open: true,
        message: t('pages:campaigns.detail.export.toast.exportFailed', { error: event.error_message || t('pages:campaigns.detail.export.toast.exportError') }),
        severity: 'error',
      });
    },
  });

  const md5 = (str: string): string => {
    function md5cycle(x: number[], k: number[]) {
      let a = x[0], b = x[1], c = x[2], d = x[3];

      a = ff(a, b, c, d, k[0], 7, -680876936);
      d = ff(d, a, b, c, k[1], 12, -389564586);
      c = ff(c, d, a, b, k[2], 17, 606105819);
      b = ff(b, c, d, a, k[3], 22, -1044525330);
      a = ff(a, b, c, d, k[4], 7, -176418897);
      d = ff(d, a, b, c, k[5], 12, 1200080426);
      c = ff(c, d, a, b, k[6], 17, -1473231341);
      b = ff(b, c, d, a, k[7], 22, -45705983);
      a = ff(a, b, c, d, k[8], 7, 1770035416);
      d = ff(d, a, b, c, k[9], 12, -1958414417);
      c = ff(c, d, a, b, k[10], 17, -42063);
      b = ff(b, c, d, a, k[11], 22, -1990404162);
      a = ff(a, b, c, d, k[12], 7, 1804603682);
      d = ff(d, a, b, c, k[13], 12, -40341101);
      c = ff(c, d, a, b, k[14], 17, -1502002290);
      b = ff(b, c, d, a, k[15], 22, 1236535329);

      a = gg(a, b, c, d, k[1], 5, -165796510);
      d = gg(d, a, b, c, k[6], 9, -1069501632);
      c = gg(c, d, a, b, k[11], 14, 643717713);
      b = gg(b, c, d, a, k[0], 20, -373897302);
      a = gg(a, b, c, d, k[5], 5, -701558691);
      d = gg(d, a, b, c, k[10], 9, 38016083);
      c = gg(c, d, a, b, k[15], 14, -660478335);
      b = gg(b, c, d, a, k[4], 20, -405537848);
      a = gg(a, b, c, d, k[9], 5, 568446438);
      d = gg(d, a, b, c, k[14], 9, -1019803690);
      c = gg(c, d, a, b, k[3], 14, -187363961);
      b = gg(b, c, d, a, k[8], 20, 1163531501);
      a = gg(a, b, c, d, k[13], 5, -1444681467);
      d = gg(d, a, b, c, k[2], 9, -51403784);
      c = gg(c, d, a, b, k[7], 14, 1735328473);
      b = gg(b, c, d, a, k[12], 20, -1926607734);

      a = hh(a, b, c, d, k[5], 4, -378558);
      d = hh(d, a, b, c, k[8], 11, -2022574463);
      c = hh(c, d, a, b, k[11], 16, 1839030562);
      b = hh(b, c, d, a, k[14], 23, -35309556);
      a = hh(a, b, c, d, k[1], 4, -1530992060);
      d = hh(d, a, b, c, k[4], 11, 1272893353);
      c = hh(c, d, a, b, k[7], 16, -155497632);
      b = hh(b, c, d, a, k[10], 23, -1094730640);
      a = hh(a, b, c, d, k[13], 4, 681279174);
      d = hh(d, a, b, c, k[0], 11, -358537222);
      c = hh(c, d, a, b, k[3], 16, -722521979);
      b = hh(b, c, d, a, k[6], 23, 76029189);
      a = hh(a, b, c, d, k[9], 4, -640364487);
      d = hh(d, a, b, c, k[12], 11, -421815835);
      c = hh(c, d, a, b, k[15], 16, 530742520);
      b = hh(b, c, d, a, k[2], 23, -995338651);

      a = ii(a, b, c, d, k[0], 6, -198630844);
      d = ii(d, a, b, c, k[7], 10, 1126891415);
      c = ii(c, d, a, b, k[14], 15, -1416354905);
      b = ii(b, c, d, a, k[5], 21, -57434055);
      a = ii(a, b, c, d, k[12], 6, 1700485571);
      d = ii(d, a, b, c, k[3], 10, -1894986606);
      c = ii(c, d, a, b, k[10], 15, -1051523);
      b = ii(b, c, d, a, k[1], 21, -2054922799);
      a = ii(a, b, c, d, k[8], 6, 1873313359);
      d = ii(d, a, b, c, k[15], 10, -30611744);
      c = ii(c, d, a, b, k[6], 15, -1560198380);
      b = ii(b, c, d, a, k[13], 21, 1309151649);
      a = ii(a, b, c, d, k[4], 6, -145523070);
      d = ii(d, a, b, c, k[11], 10, -1120210379);
      c = ii(c, d, a, b, k[2], 15, 718787259);
      b = ii(b, c, d, a, k[9], 21, -343485551);

      x[0] = add32(a, x[0]);
      x[1] = add32(b, x[1]);
      x[2] = add32(c, x[2]);
      x[3] = add32(d, x[3]);
    }

    function cmn(q: number, a: number, b: number, x: number, s: number, t: number) {
      a = add32(add32(a, q), add32(x, t));
      return add32((a << s) | (a >>> (32 - s)), b);
    }

    function ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
      return cmn((b & c) | ((~b) & d), a, b, x, s, t);
    }

    function gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
      return cmn((b & d) | (c & (~d)), a, b, x, s, t);
    }

    function hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
      return cmn(b ^ c ^ d, a, b, x, s, t);
    }

    function ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
      return cmn(c ^ (b | (~d)), a, b, x, s, t);
    }

    function add32(a: number, b: number) {
      return (a + b) & 0xFFFFFFFF;
    }

    function rhex(n: number) {
      let s = '', j = 0;
      for (; j < 4; j++) {
        s += hex_chr[(n >> (j * 8 + 4)) & 0x0F] + hex_chr[(n >> (j * 8)) & 0x0F];
      }
      return s;
    }

    const hex_chr = '0123456789abcdef'.split('');

    const s = str;
    const n = s.length * 8;
    const x: number[] = [];
    let i: number;

    for (i = 0; i < s.length; i++) {
      x[i >> 2] |= (s.charCodeAt(i) & 0xFF) << ((i % 4) * 8);
    }

    x[i >> 2] |= 0x80 << ((i % 4) * 8);
    x[(((n + 64) >>> 9) << 4) + 14] = n;

    const h = [1732584193, -271733879, -1732584194, 271733878];
    for (i = 0; i < x.length; i += 16) {
      const olda = h[0], oldb = h[1], oldc = h[2], oldd = h[3];
      h[0] = olda; h[1] = oldb; h[2] = oldc; h[3] = oldd;
      md5cycle(h, x.slice(i, i + 16));
      h[0] = add32(h[0], olda);
      h[1] = add32(h[1], oldb);
      h[2] = add32(h[2], oldc);
      h[3] = add32(h[3], oldd);
    }

    return rhex(h[0]) + rhex(h[1]) + rhex(h[2]) + rhex(h[3]);
  };

  const hasStatsChanged = useMemo(() => {
    if (!stats || reports.length === 0) {
      return true;
    }

    const lastCompletedReport = reports.find((report) => report.status === 'completed');
    if (!lastCompletedReport || !lastCompletedReport.statsHash) {
      return true;
    }

    const statsDict: Record<string, number> = {
      total: stats.totalContacts,
      pending: stats.pendingContacts,
      in_progress: stats.inProgressContacts,
      reached: stats.reachedContacts,
      optout: stats.optoutContacts,
      mismatch: stats.mismatchContacts,
      exhausted: stats.exhaustedContacts,
      no_valid_phones: stats.noValidPhonesContacts || 0,
      skipped: stats.skippedContacts || 0,
      converted: stats.convertedContacts,
      total_calls: stats.totalCalls,
      pending_calls: stats.pendingCalls,
      in_progress_calls: stats.inProgressCalls,
      connected_calls: stats.connectedCalls,
      conversion_eligible_calls: stats.conversionEligibleCalls,
      failed_calls: stats.failedCalls,
      no_answer_calls: stats.noAnswer || 0,
      busy_calls: stats.busy || 0,
      cancelled_calls: stats.cancelled || 0,
      converted_calls: stats.convertedCalls,
    };

    const statsJson = JSON.stringify(statsDict, Object.keys(statsDict).sort());
    const currentHash = md5(statsJson);
    return currentHash !== lastCompletedReport.statsHash;
  }, [stats, reports]);

  const hasCalls = (stats?.totalCalls ?? 0) > 0;
  const reportDisabled = isGenerating || loadingActiveTenantReport || Boolean(activeTenantReport) || !hasStatsChanged || !hasCalls;

  const handleRequestReport = async () => {
    if (!campaignId) {
      return;
    }
    try {
      setIsGenerating(true);
      const response = await api.post(`/api/campaigns/${campaignId}/report/`);
      const reportId = response.data?.reportId || response.data?.report_id;
      if (reportId) {
        setActiveReportId(reportId);
      }
      setReportToast({
        open: true,
        message: t('pages:campaigns.detail.export.toast.exportStarted'),
        severity: 'info',
      });
      await fetchReports();
    } catch (err: unknown) {
      const lockedReport = extractActiveTenantReportFromError(err);
      if (lockedReport) {
        setActiveTenantReport(lockedReport);
        setIsGenerating(false);
        return;
      }
      const error = err as { response?: { data?: { error?: string } } };
      setReportToast({
        open: true,
        message:
          error?.response?.data?.error
          || extractApiDetailMessage(err)
          || t('pages:campaigns.detail.export.toast.exportError'),
        severity: 'error',
      });
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download />
            {t('pages:campaigns.detail.export.title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {reports.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('pages:campaigns.detail.export.recentExports')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {reports.slice(0, 5).map((report) => (
                  <Paper key={report.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="medium" noWrap title={report.fileName || t('pages:campaigns.detail.export.fileName')}>
                          {report.fileName || t('pages:campaigns.detail.export.fileName')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.createdAt
                            ? formatDateTime(report.createdAt, { locale })
                            : ''}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={t(`pages:campaigns.detail.export.status.${report.status}` as never)}
                        color={
                          report.status === 'completed' ? 'success' :
                          report.status === 'failed' ? 'error' :
                          report.status === 'processing' ? 'warning' : 'default'
                        }
                        sx={{ ml: 1 }}
                      />
                    </Box>

                    {report.status === 'completed' && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {formatNumber(report.totalContacts)} {t('pages:campaigns.detail.export.contacts')} • {formatNumber(report.totalCalls)} {t('pages:campaigns.detail.export.calls')}
                          {report.fileSize && ` • ${t('pages:campaigns.detail.export.fileSize', { size: formatDecimal(report.fileSize / 1024 / 1024) })}`}
                          {report.startedAt && report.completedAt && (() => {
                            const duration = Math.round((new Date(report.completedAt).getTime() - new Date(report.startedAt).getTime()) / 1000);
                            const minutes = Math.floor(duration / 60);
                            const seconds = duration % 60;
                            return minutes > 0
                              ? ` • ${t('pages:campaigns.detail.export.generatedIn', { minutes, seconds })}`
                              : ` • ${t('pages:campaigns.detail.export.generatedInSeconds', { seconds })}`;
                          })()}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Download />}
                          disabled={downloadingReportId === report.id}
                          onClick={() => {
                            void handleDownload(report.id);
                          }}
                        >
                          {t('pages:campaigns.detail.export.download')}
                        </Button>
                      </Box>
                    )}

                    {report.status === 'failed' && report.errorMessage && (
                      <Alert severity="error" sx={{ mt: 1 }} icon={false}>
                        <Typography variant="caption">{report.errorMessage}</Typography>
                      </Alert>
                    )}

                    {(report.status === 'pending' || report.status === 'processing') && (
                      <Box sx={{ mt: 1 }}>
                        {report.progress != null && report.progress > 0 ? (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <LinearProgress
                                variant={report.progressMessage ? 'indeterminate' : 'determinate'}
                                value={report.progress}
                                sx={{ flex: 1, height: 6, borderRadius: 3 }}
                              />
                              <Typography variant="caption" fontWeight="medium" sx={{ minWidth: 40 }}>
                                {formatPercent(report.progress ?? 0)}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {report.progressMessage ? (
                                report.progressMessage
                              ) : (
                                <>
                                  {t('pages:campaigns.detail.export.processed', {
                                    processed: formatNumber(report.processedContacts ?? 0),
                                    total: report.totalContacts ? formatNumber(report.totalContacts) : '?',
                                  })}
                                  {report.estimatedSecondsRemaining != null && report.estimatedSecondsRemaining > 0 && (
                                    <> • {report.estimatedSecondsRemaining > 60
                                      ? t('pages:campaigns.detail.export.eta', {
                                          minutes: Math.floor(report.estimatedSecondsRemaining / 60),
                                          seconds: report.estimatedSecondsRemaining % 60,
                                        })
                                      : t('pages:campaigns.detail.export.etaSeconds', { seconds: report.estimatedSecondsRemaining })
                                    }</>
                                  )}
                                </>
                              )}
                            </Typography>
                          </>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={14} />
                            <Typography variant="caption" color="text.secondary">
                              {report.status === 'pending' ? t('pages:campaigns.detail.export.waiting') : t('pages:campaigns.detail.export.starting')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('pages:campaigns.detail.export.noPreviousExports')}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {activeTenantReport && (
            <Box sx={{ mb: 2 }}>
              <ActiveTenantReportAlert report={activeTenantReport} locale={locale} />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('pages:campaigns.detail.export.generateNewExport')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('pages:campaigns.detail.export.close')}</Button>
          <Tooltip title={reportDisabled && !hasCalls ? t('pages:campaigns.detail.export.noCallsTooltip') : ''} arrow>
            <Box>
              <Button
                variant="contained"
                onClick={handleRequestReport}
                disabled={reportDisabled}
                startIcon={isGenerating ? <CircularProgress size={16} /> : <Refresh />}
              >
                {isGenerating ? t('pages:campaigns.detail.export.generating') : t('pages:campaigns.detail.export.generateNewExportButton')}
              </Button>
            </Box>
          </Tooltip>
          {!hasStatsChanged && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {t('pages:campaigns.detail.export.noChanges')}
            </Typography>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={reportToast.open}
        autoHideDuration={6000}
        onClose={() => setReportToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={reportToast.severity}
          onClose={() => setReportToast((prev) => ({ ...prev, open: false }))}
        >
          {reportToast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
